import React, { useCallback, useMemo, useRef, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useUpdateBundleRules } from '~/hooks/apis/accommodation/useBundles';

import {
  Bundle,
  BundleGuestOccupancy,
  BundleHotelRule,
  UpdateBundleRulesPayload,
} from '~/services/AccommodationService';

import { ALL_BOARD_CODES, ALL_CANCELLATION_POLICIES, BOARD_CODE_LABELS, CANCELLATION_POLICY_LABELS } from './constants';

type BoardCode = BundleHotelRule['boardCodes'][number];
type CancellationPolicy = BundleHotelRule['cancellationPolicies'][number];

interface Props {
  bundle: Bundle;
}

export default function BundleRulesSection({ bundle }: Props) {
  const rule = bundle.rules[0];

  const [losMin, setLosMin] = useState(rule?.losMin ?? 1);
  const [leadDaysMin, setLeadDaysMin] = useState(rule?.leadDaysMin ?? 1);
  const [boardCodes, setBoardCodes] = useState<Array<BoardCode>>(rule?.boardCodes ?? ALL_BOARD_CODES);
  const [cancellationPolicies, setCancellationPolicies] = useState<Array<CancellationPolicy>>(
    rule?.cancellationPolicies ?? ALL_CANCELLATION_POLICIES,
  );
  const occupancyIdRef = useRef(rule?.includedGuests?.length ?? 1);
  const [includedGuests, setIncludedGuests] = useState(
    (rule?.includedGuests ?? [{ adults: 2, children: 0, infants: 0 }]).map((g, i) => ({
      ...g,
      _key: i,
    })),
  );

  const updateRulesMutation = useUpdateBundleRules();

  const handleBoardCodeToggle = useCallback((code: BoardCode) => {
    setBoardCodes((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  }, []);

  const handleCancellationPolicyToggle = useCallback((policy: CancellationPolicy) => {
    setCancellationPolicies((prev) => (prev.includes(policy) ? prev.filter((p) => p !== policy) : [...prev, policy]));
  }, []);

  const handleGuestChange = useCallback((key: number, field: keyof BundleGuestOccupancy, value: number) => {
    setIncludedGuests((prev) => prev.map((g) => (g._key === key ? { ...g, [field]: value } : g)));
  }, []);

  const handleAddOccupancy = useCallback(() => {
    const nextKey = occupancyIdRef.current++;
    setIncludedGuests((prev) => [...prev, { adults: 2, children: 0, infants: 0, _key: nextKey }]);
  }, []);

  const handleRemoveOccupancy = useCallback((key: number) => {
    setIncludedGuests((prev) => prev.filter((g) => g._key !== key));
  }, []);

  const hasChanges = useMemo(() => {
    const originalGuests = rule?.includedGuests ?? [{ adults: 2, children: 0, infants: 0 }];
    const currentGuests = includedGuests.map(({ _key, ...rest }) => rest);
    return (
      losMin !== (rule?.losMin ?? 1) ||
      leadDaysMin !== (rule?.leadDaysMin ?? 1) ||
      JSON.stringify(boardCodes) !== JSON.stringify(rule?.boardCodes ?? ALL_BOARD_CODES) ||
      JSON.stringify(cancellationPolicies) !==
        JSON.stringify(rule?.cancellationPolicies ?? ALL_CANCELLATION_POLICIES) ||
      JSON.stringify(currentGuests) !== JSON.stringify(originalGuests)
    );
  }, [rule, losMin, leadDaysMin, boardCodes, cancellationPolicies, includedGuests]);

  const handleSave = useCallback(() => {
    const payload: UpdateBundleRulesPayload = {
      losMin,
      leadDaysMin,
      boardCodes,
      cancellationPolicies,
      includedGuests: includedGuests.map(({ _key, ...rest }) => rest),
    };
    updateRulesMutation.mutate({ bundleId: bundle.id, payload });
  }, [bundle.id, losMin, leadDaysMin, boardCodes, cancellationPolicies, includedGuests, updateRulesMutation]);

  if (bundle.rules.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No rules configured for this bundle.
      </Typography>
    );
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={3}>
        <TextField
          label="Min Length of Stay (nights)"
          type="number"
          value={losMin}
          onChange={(e) => setLosMin(Math.max(1, Number(e.target.value)))}
          inputProps={{ min: 1 }}
          size="small"
          sx={{ width: 220 }}
        />
        <TextField
          label="Min Lead Days"
          type="number"
          value={leadDaysMin}
          onChange={(e) => setLeadDaysMin(Math.max(1, Number(e.target.value)))}
          inputProps={{ min: 1 }}
          size="small"
          sx={{ width: 220 }}
        />
      </Stack>

      <Box>
        <FormLabel component="legend">Applicable Board Codes</FormLabel>
        <FormGroup row>
          {ALL_BOARD_CODES.map((code) => (
            <FormControlLabel
              key={code}
              control={<Checkbox checked={boardCodes.includes(code)} onChange={() => handleBoardCodeToggle(code)} />}
              label={BOARD_CODE_LABELS[code]}
            />
          ))}
        </FormGroup>
      </Box>

      <Box>
        <FormLabel component="legend">Cancellation Policies</FormLabel>
        <FormGroup row>
          {ALL_CANCELLATION_POLICIES.map((policy) => (
            <FormControlLabel
              key={policy}
              control={
                <Checkbox
                  checked={cancellationPolicies.includes(policy)}
                  onChange={() => handleCancellationPolicyToggle(policy)}
                />
              }
              label={CANCELLATION_POLICY_LABELS[policy]}
            />
          ))}
        </FormGroup>
      </Box>

      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <FormLabel component="legend">Included Guest Occupancies</FormLabel>
          <Button variant="text" size="small" onClick={handleAddOccupancy}>
            Add Occupancy
          </Button>
        </Stack>
        <Stack spacing={2}>
          {includedGuests.map((occupancy, index) => (
            <Stack key={occupancy._key} direction="row" spacing={2} alignItems="center">
              <Chip label={`#${index + 1}`} size="small" />
              <TextField
                label="Adults"
                type="number"
                value={occupancy.adults}
                onChange={(e) => handleGuestChange(occupancy._key, 'adults', Math.max(0, Number(e.target.value)))}
                inputProps={{ min: 0 }}
                size="small"
                sx={{ width: 100 }}
              />
              <TextField
                label="Children"
                type="number"
                value={occupancy.children}
                onChange={(e) => handleGuestChange(occupancy._key, 'children', Math.max(0, Number(e.target.value)))}
                inputProps={{ min: 0 }}
                size="small"
                sx={{ width: 100 }}
              />
              <TextField
                label="Infants"
                type="number"
                value={occupancy.infants}
                onChange={(e) => handleGuestChange(occupancy._key, 'infants', Math.max(0, Number(e.target.value)))}
                inputProps={{ min: 0 }}
                size="small"
                sx={{ width: 100 }}
              />
              {includedGuests.length > 1 && (
                <Button variant="text" color="error" size="small" onClick={() => handleRemoveOccupancy(occupancy._key)}>
                  Remove
                </Button>
              )}
            </Stack>
          ))}
        </Stack>
      </Box>

      <Box>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={
            updateRulesMutation.isPending || !hasChanges || boardCodes.length === 0 || cancellationPolicies.length === 0
          }
        >
          {updateRulesMutation.isPending ? 'Saving...' : 'Save Rules'}
        </Button>
      </Box>
    </Stack>
  );
}
